import {
  CLIENT_EVENTS,
  SERVER_EVENTS,
  type AckDTO,
  type ConnectionRole,
  type Locale,
  type RoomSnapshotDTO,
  type SessionDTO,
  type TimerDTO,
} from '@devils-paw/protocol';
import { io, type Socket } from 'socket.io-client';

export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'offline';

export interface GameStoreState {
  connection: ConnectionState;
  snapshot: RoomSnapshotDTO | null;
  session: SessionDTO | null;
  errorKey: string | null;
  clockOffsetMs: number;
  missedPhase: boolean;
}

type Listener = () => void;
type AckResolver = (ack: AckDTO) => void;

interface SocketLike {
  readonly connected: boolean;
  on(event: string, listener: (payload?: unknown) => void): this;
  emit(event: string, payload: Record<string, unknown>, ack: (value: AckDTO) => void): this;
  connect(): this;
  disconnect(): this;
  io?: { on(event: string, listener: () => void): void };
}

interface PendingAction {
  event: string;
  payload: Record<string, unknown>;
  resolve: AckResolver;
  attempts: number;
  timeoutId: ReturnType<typeof setTimeout> | null;
}

interface StoredSession {
  participantId: string;
  reconnectToken: string;
  expiresAt: number;
  role?: ConnectionRole;
  lastPhaseId?: string;
}

export interface GameSocketStoreOptions {
  socketFactory?: () => SocketLike;
  now?: () => number;
  storage?: Storage;
  retryMs?: number;
}

const STORAGE_PREFIX = 'devils-paw:session:';
const DRAFT_PREFIX = 'devils-paw:draft:';

export function normalizeRoomCode(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

export function clockOffsetFrom(serverNow: number, receivedAt: number): number {
  return serverNow - receivedAt;
}

export function remainingFromTimer(timer: TimerDTO | null, clientNow: number, clockOffsetMs: number): number | null {
  if (timer?.deadlineAt === null || timer === null) return null;
  const serverReference = timer.paused ? timer.serverNow : clientNow + clockOffsetMs;
  return Math.max(0, timer.deadlineAt - serverReference);
}

let actionIdFallbackCounter = 0;

export function createActionId(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const words = new Uint32Array(4);
    crypto.getRandomValues(words);
    return `action-${Array.from(words, (word) => word.toString(16).padStart(8, '0')).join('')}`;
  }
  actionIdFallbackCounter += 1;
  return `action-${Date.now()}-${actionIdFallbackCounter}`;
}

function adaptSocket(socket: Socket): SocketLike {
  const adapter: SocketLike = {
    get connected() { return socket.connected; },
    on(event, listener) {
      socket.on(event, (payload?: unknown) => listener(payload));
      return adapter;
    },
    emit(event, payload, ack) {
      socket.emit(event, payload, ack);
      return adapter;
    },
    connect() { socket.connect(); return adapter; },
    disconnect() { socket.disconnect(); return adapter; },
    io: { on(event, listener) { if (event === 'reconnect_attempt') socket.io.on('reconnect_attempt', listener); } },
  };
  return adapter;
}

function reasonToKey(ack: AckDTO): string {
  if (ack.status === 'stale-phase') return 'error.stalePhase';
  if (ack.status === 'rate-limited') return 'error.rateLimited';
  if (ack.status === 'forbidden') {
    if (ack.reason === 'invalid-reconnect-token') return 'error.invalidReconnect';
    if (ack.reason === 'pairing-token-expired') return 'error.pairingExpired';
    if (ack.reason === 'pairing-token-used') return 'error.pairingUsed';
    if (ack.reason === 'invalid-pairing-token' || ack.reason === 'pairing-participant-gone') return 'error.pairingInvalid';
    if (ack.reason === 'time-scale-production') return 'error.demoSpeedProduction';
    return 'error.forbidden';
  }
  if (ack.reason === 'room-not-found') return 'error.roomNotFound';
  if (ack.reason === 'room-full') return 'error.roomFull';
  if (ack.reason === 'game-in-progress') return 'error.gameStarted';
  return 'error.generic';
}

export class GameSocketStore {
  private readonly listeners = new Set<Listener>();
  private readonly socketFactory: () => SocketLike;
  private readonly now: () => number;
  private readonly storage: Storage | undefined;
  private readonly retryMs: number;
  private socket: SocketLike | null = null;
  private roomCode: string | null = null;
  private pendingReconnect = false;
  private readonly pending = new Map<string, PendingAction>();
  private draftTimer: ReturnType<typeof setTimeout> | null = null;
  private queuedDraft: { event: string; body: Record<string, unknown> } | null = null;
  private state: GameStoreState = {
    connection: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'connecting',
    snapshot: null,
    session: null,
    errorKey: null,
    clockOffsetMs: 0,
    missedPhase: false,
  };

  constructor(options: GameSocketStoreOptions = {}) {
    this.socketFactory = options.socketFactory ?? (() => {
      const configuredUrl: unknown = import.meta.env.VITE_SERVER_URL;
      const desktopUrl = typeof window !== 'undefined' ? window.devilsPaw?.getServerUrl() : undefined;
      const serverUrl = desktopUrl ?? (typeof configuredUrl === 'string' && configuredUrl.length > 0 ? configuredUrl : undefined);
      return adaptSocket(io(serverUrl, {
        autoConnect: false,
        reconnection: true,
        transports: ['websocket', 'polling'],
      }));
    });
    this.now = options.now ?? Date.now;
    this.storage = options.storage ?? (typeof localStorage === 'undefined' ? undefined : localStorage);
    this.retryMs = options.retryMs ?? 2_000;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getSnapshot = (): GameStoreState => this.state;

  private update(patch: Partial<GameStoreState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private handleOnline = (): void => {
    this.update({ connection: this.socket?.connected ? 'connected' : 'reconnecting' });
    this.ensureSocket().connect();
  };

  private handleOffline = (): void => {
    this.update({ connection: 'offline' });
  };

  private ensureSocket(): SocketLike {
    if (this.socket !== null) return this.socket;
    const socket = this.socketFactory();
    this.socket = socket;
    socket.on('connect', () => {
      this.update({ connection: 'connected', errorKey: null });
      if (this.pendingReconnect) void this.reconnectStored().then(() => this.flushPending());
      else this.flushPending();
    });
    socket.on('disconnect', () => {
      if (this.roomCode !== null && this.readSession(this.roomCode) !== null) this.pendingReconnect = true;
      this.update({ connection: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'reconnecting' });
    });
    socket.on('connect_error', () => this.update({
      connection: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'reconnecting',
      errorKey: 'error.network',
    }));
    socket.io?.on('reconnect_attempt', () => this.update({ connection: 'reconnecting' }));
    socket.on(SERVER_EVENTS.sessionCreated, (session) => this.acceptSession(session as SessionDTO));
    socket.on(SERVER_EVENTS.roomSnapshot, (snapshot) => this.acceptSnapshot(snapshot as RoomSnapshotDTO));
    socket.on(SERVER_EVENTS.phaseChanged, (snapshot) => this.acceptSnapshot(snapshot as RoomSnapshotDTO));
    socket.on(SERVER_EVENTS.publicError, (error) => {
      const messageKey = typeof error === 'object' && error !== null && 'messageKey' in error && typeof error.messageKey === 'string'
        ? error.messageKey
        : 'error.generic';
      this.update({ errorKey: messageKey });
    });
    return socket;
  }

  connect(): void {
    const socket = this.ensureSocket();
    if (!socket.connected) {
      this.update({ connection: typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'connecting' });
      socket.connect();
    }
  }

  async roomExists(rawCode: string): Promise<boolean> {
    const code = normalizeRoomCode(rawCode);
    if (code.length !== 5) return false;
    const configuredUrl: unknown = import.meta.env.VITE_SERVER_URL;
    const desktopUrl = typeof window !== 'undefined' ? window.devilsPaw?.getServerUrl() : undefined;
    const base = desktopUrl ?? (typeof configuredUrl === 'string' ? configuredUrl : '');
    const response = await fetch(`${base}/api/rooms/${encodeURIComponent(code)}/exists`);
    if (!response.ok) throw new Error('room-exists-unavailable');
    const body: unknown = await response.json();
    return typeof body === 'object' && body !== null && 'exists' in body && body.exists === true;
  }

  async createRoom(nickname: string, avatar: string, locale: Locale): Promise<AckDTO> {
    this.connect();
    return this.dispatch(CLIENT_EVENTS.roomCreate, {
      expectedPhaseId: 'bootstrap', nickname, avatar, locale, role: 'player', platform: 'web',
    });
  }

  async joinRoom(rawCode: string, nickname: string, avatar: string): Promise<AckDTO> {
    const code = normalizeRoomCode(rawCode);
    this.roomCode = code;
    this.connect();
    return this.dispatch(CLIENT_EVENTS.roomJoin, {
      expectedPhaseId: 'bootstrap', code, nickname, avatar, role: 'player', platform: 'web',
    });
  }

  async joinViewer(rawCode: string, nickname: string, role: 'shared-display' | 'spectator'): Promise<AckDTO> {
    const code = normalizeRoomCode(rawCode);
    this.roomCode = code;
    this.connect();
    return this.dispatch(CLIENT_EVENTS.roomJoin, {
      expectedPhaseId: 'bootstrap', code, nickname, role, platform: 'web',
    });
  }

  async pairController(rawCode: string, pairingToken: string): Promise<AckDTO> {
    const code = normalizeRoomCode(rawCode);
    this.roomCode = code;
    this.connect();
    return this.dispatch(CLIENT_EVENTS.controllerPair, {
      expectedPhaseId: 'bootstrap', code, pairingToken, platform: 'web',
    });
  }

  issuePairing(): Promise<AckDTO> {
    return this.action(CLIENT_EVENTS.controllerIssuePairing);
  }

  unpairController(): Promise<AckDTO> {
    return this.action(CLIENT_EVENTS.controllerUnpair);
  }

  createDemo(speed: number): Promise<AckDTO> {
    this.connect();
    return this.dispatch(CLIENT_EVENTS.demoCreate, {
      expectedPhaseId: 'bootstrap', speed, platform: 'web',
    });
  }

  restoreRoom(rawCode: string): boolean {
    const code = normalizeRoomCode(rawCode);
    this.roomCode = code;
    const stored = this.readSession(code);
    if (stored === null || stored.expiresAt <= this.now()) return false;
    this.pendingReconnect = true;
    this.connect();
    if (this.socket?.connected) void this.reconnectStored();
    return true;
  }

  private async reconnectStored(): Promise<void> {
    if (this.roomCode === null) return;
    const stored = this.readSession(this.roomCode);
    if (stored === null) return;
    this.pendingReconnect = false;
    const previousPhaseId = stored.lastPhaseId;
    const ack = await this.dispatch(CLIENT_EVENTS.roomReconnect, {
      expectedPhaseId: this.state.snapshot?.phaseId ?? 'bootstrap',
      code: this.roomCode,
      participantId: stored.participantId,
      reconnectToken: stored.reconnectToken,
      platform: 'web',
    });
    const data = ack.data as { snapshot?: RoomSnapshotDTO } | undefined;
    if (
      (ack.status === 'accepted' || ack.status === 'duplicate') &&
      previousPhaseId !== undefined &&
      data?.snapshot !== undefined &&
      data.snapshot.phaseId !== previousPhaseId
    ) {
      this.update({ missedPhase: true });
    }
  }

  dispatch(event: string, body: Record<string, unknown>, actionId = createActionId()): Promise<AckDTO> {
    return new Promise<AckDTO>((resolve) => {
      const payload = { ...body, actionId };
      const action: PendingAction = { event, payload, resolve, attempts: 0, timeoutId: null };
      this.pending.set(actionId, action);
      this.emitPending(actionId);
    });
  }

  action(event: string, body: Record<string, unknown> = {}): Promise<AckDTO> {
    return this.dispatch(event, {
      expectedPhaseId: this.state.snapshot?.phaseId ?? 'bootstrap',
      ...body,
    });
  }

  private emitPending(actionId: string): void {
    const action = this.pending.get(actionId);
    const socket = this.ensureSocket();
    if (action === undefined) return;
    if (!socket.connected) {
      socket.connect();
      return;
    }
    action.attempts += 1;
    if (action.timeoutId !== null) clearTimeout(action.timeoutId);
    socket.emit(action.event, action.payload, (ack: AckDTO) => {
      const current = this.pending.get(actionId);
      if (current === undefined) return;
      if (current.timeoutId !== null) clearTimeout(current.timeoutId);
      this.pending.delete(actionId);
      if (ack.status !== 'accepted' && ack.status !== 'duplicate') this.update({ errorKey: reasonToKey(ack) });
      const data = ack.data as { session?: SessionDTO; snapshot?: RoomSnapshotDTO } | undefined;
      if (data?.session !== undefined) this.acceptSession(data.session);
      if (data?.snapshot !== undefined) this.acceptSnapshot(data.snapshot);
      current.resolve(ack);
    });
    action.timeoutId = setTimeout(() => {
      const current = this.pending.get(actionId);
      if (current === undefined) return;
      this.emitPending(actionId);
    }, this.retryMs);
  }

  private flushPending(): void {
    for (const actionId of this.pending.keys()) this.emitPending(actionId);
  }

  private acceptSession(session: SessionDTO): void {
    this.roomCode = session.roomCode;
    const previous = this.readSession(session.roomCode);
    this.storage?.setItem(`${STORAGE_PREFIX}${session.roomCode}`, JSON.stringify({
      participantId: session.participantId,
      reconnectToken: session.reconnectToken,
      expiresAt: session.expiresAt,
      role: session.role,
      lastPhaseId: previous?.lastPhaseId,
    } satisfies StoredSession));
    this.update({ session });
  }

  private acceptSnapshot(snapshot: RoomSnapshotDTO): void {
    const receivedAt = this.now();
    this.roomCode = snapshot.code;
    const serverDraft = snapshot.privateView?.draft;
    if (serverDraft !== null && serverDraft !== undefined) this.storeDraft(snapshot.phaseId, serverDraft);
    const stored = this.readSession(snapshot.code);
    if (stored !== null) {
      this.storage?.setItem(`${STORAGE_PREFIX}${snapshot.code}`, JSON.stringify({
        ...stored,
        lastPhaseId: snapshot.phaseId,
      } satisfies StoredSession));
    }
    this.update({
      snapshot,
      clockOffsetMs: clockOffsetFrom(snapshot.timer.serverNow, receivedAt),
      errorKey: null,
    });
  }

  private readSession(code: string): StoredSession | null {
    const raw = this.storage?.getItem(`${STORAGE_PREFIX}${code}`);
    if (raw === null || raw === undefined) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<StoredSession>;
      return typeof parsed.participantId === 'string' && typeof parsed.reconnectToken === 'string' && typeof parsed.expiresAt === 'number'
        ? parsed as StoredSession
        : null;
    } catch {
      return null;
    }
  }

  private draftKey(phaseId: string): string {
    return `${DRAFT_PREFIX}${this.roomCode ?? 'unknown'}:${phaseId}`;
  }

  storeDraft(phaseId: string, text: string): void {
    this.storage?.setItem(this.draftKey(phaseId), text);
  }

  recoveredDraft(phaseId = this.state.snapshot?.phaseId, serverDraft = this.state.snapshot?.privateView?.draft): string {
    if (phaseId === undefined) return serverDraft ?? '';
    return serverDraft ?? this.storage?.getItem(this.draftKey(phaseId)) ?? '';
  }

  syncDraft(event: string, body: Record<string, unknown>, delayMs = 350): void {
    this.queuedDraft = { event, body };
    const phaseId = this.state.snapshot?.phaseId;
    if (phaseId !== undefined && typeof body.text === 'string') this.storeDraft(phaseId, body.text);
    if (this.draftTimer !== null) return;
    this.draftTimer = setTimeout(() => {
      this.draftTimer = null;
      const draft = this.queuedDraft;
      this.queuedDraft = null;
      if (draft !== null) void this.action(draft.event, draft.body);
    }, delayMs);
  }

  clearError(): void {
    this.update({ errorKey: null });
  }

  clearMissedPhase(): void {
    this.update({ missedPhase: false });
  }

  disconnect(): void {
    this.socket?.disconnect();
  }
}

export const gameStore = new GameSocketStore();
